<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">My Profile</h5>
        <p class="page-sub">Your employee information and documents</p>
      </div>
    </div>

    <div v-if="!employeeId" class="no-profile-box">
      <font-awesome-icon icon="user-slash" class="no-profile-icon" />
      <p class="no-profile-title">No employee profile linked</p>
      <p class="no-profile-sub">Contact your HR administrator to link your account to an employee record.</p>
    </div>

    <template v-else>
      <!-- Skeleton -->
      <div v-if="loading" class="sk-profile">
        <div class="sk-avatar"></div>
        <div class="sk-lines">
          <div class="sk-line sk-line--lg"></div>
          <div class="sk-line sk-line--sm"></div>
          <div class="sk-line sk-line--md"></div>
        </div>
      </div>

      <template v-else-if="profile">
        <!-- Avatar card -->
        <div class="profile-hero">
          <div class="avatar-wrap">
            <img v-if="profile.photo" :src="profile.photo" class="avatar-img" :alt="fullName" />
            <div v-else class="avatar-initials">{{ initials }}</div>
          </div>
          <div class="profile-hero-info">
            <h4 class="profile-name">{{ fullName }}</h4>
            <p class="profile-role">{{ profile.position?.name }}</p>
            <div class="profile-tags">
              <span class="tag tag--dept" v-if="profile.department">{{ profile.department?.name }}</span>
              <span class="tag tag--grade" v-if="profile.paygrade">{{ profile.paygrade?.name }}</span>
              <span class="tag" :class="profile.employment_status === 'active' ? 'tag--green' : 'tag--gray'">
                {{ profile.employment_status || 'Active' }}
              </span>
            </div>
          </div>
        </div>

        <!-- Sections -->
        <div class="sections">

          <!-- Personal Details -->
          <div class="section-card">
            <h6 class="section-title">Personal Details</h6>
            <div class="details-grid">
              <div class="detail-item">
                <span class="detail-label">Employee No.</span>
                <span class="detail-val">{{ profile.employee_no || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Full Name</span>
                <span class="detail-val">{{ fullName }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Email</span>
                <span class="detail-val">{{ profile.email || profile.user?.email || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Phone</span>
                <span class="detail-val">{{ profile.phone_number || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Date of Birth</span>
                <span class="detail-val">{{ fmtDate(profile.date_of_birth) }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Gender</span>
                <span class="detail-val" style="text-transform:capitalize">{{ profile.gender || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Date Hired</span>
                <span class="detail-val">{{ fmtDate(profile.date_hired) }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Address</span>
                <span class="detail-val">{{ profile.address || '—' }}</span>
              </div>
            </div>
          </div>

          <!-- Work Details -->
          <div class="section-card">
            <h6 class="section-title">Work Details</h6>
            <div class="details-grid">
              <div class="detail-item">
                <span class="detail-label">Department</span>
                <span class="detail-val">{{ profile.department?.name || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Unit</span>
                <span class="detail-val">{{ profile.unit?.name || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Position</span>
                <span class="detail-val">{{ profile.position?.name || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Pay Grade</span>
                <span class="detail-val">{{ profile.paygrade?.name || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Location</span>
                <span class="detail-val">{{ profile.location?.name || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Supervisor</span>
                <span class="detail-val">{{ supervisorName }}</span>
              </div>
            </div>
          </div>

          <!-- Bank Details -->
          <div class="section-card" v-if="profile.bank">
            <h6 class="section-title">Bank Details</h6>
            <div class="details-grid">
              <div class="detail-item">
                <span class="detail-label">Bank Name</span>
                <span class="detail-val">{{ profile.bank?.bank_name || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Account Number</span>
                <span class="detail-val">{{ profile.bank?.account_number || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Account Name</span>
                <span class="detail-val">{{ profile.bank?.account_name || '—' }}</span>
              </div>
              <div class="detail-item" v-if="profile.pension_company">
                <span class="detail-label">Pension Company</span>
                <span class="detail-val">{{ profile.pension_company?.name || '—' }}</span>
              </div>
            </div>
          </div>

          <!-- Next of Kin -->
          <div class="section-card" v-if="profile.nextofkin">
            <h6 class="section-title">Next of Kin</h6>
            <div class="details-grid">
              <div class="detail-item">
                <span class="detail-label">Name</span>
                <span class="detail-val">{{ profile.nextofkin?.name || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Relationship</span>
                <span class="detail-val">{{ profile.nextofkin?.relationship || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Phone</span>
                <span class="detail-val">{{ profile.nextofkin?.phone || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Email</span>
                <span class="detail-val">{{ profile.nextofkin?.email || '—' }}</span>
              </div>
            </div>
          </div>

          <!-- Documents -->
          <div class="section-card" v-if="profile.documents && profile.documents.length">
            <h6 class="section-title">Documents</h6>
            <div class="docs-list">
              <a
                v-for="doc in profile.documents"
                :key="doc.id"
                :href="doc.url || doc.path"
                target="_blank"
                class="doc-item"
              >
                <div class="doc-icon">
                  <font-awesome-icon icon="file-alt" />
                </div>
                <div class="doc-info">
                  <p class="doc-name">{{ doc.name || doc.original_name || 'Document' }}</p>
                  <p class="doc-type">{{ doc.type || doc.mime_type || '' }}</p>
                </div>
                <font-awesome-icon icon="external-link-alt" class="doc-ext" />
              </a>
            </div>
          </div>

        </div>
      </template>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { useStore } from 'vuex';

export default {
  name: 'MyProfilePage',
  data() {
    return {
      store: useStore(),
      loading: false,
      profile: null,
    };
  },
  computed: {
    employeeId() {
      return this.store.state.getLoggedInUser?.employee_id || null;
    },
    fullName() {
      if (!this.profile) return '';
      const { first_name, last_name, other_name } = this.profile;
      return [first_name, other_name, last_name].filter(Boolean).join(' ');
    },
    initials() {
      if (!this.profile) return '?';
      return [(this.profile.first_name || '')[0], (this.profile.last_name || '')[0]].filter(Boolean).join('').toUpperCase();
    },
    supervisorName() {
      const s = this.profile?.supervisor;
      if (!s) return '—';
      return [s.first_name, s.last_name].filter(Boolean).join(' ') || '—';
    },
  },
  mounted() {
    if (this.employeeId) this.fetchProfile();
  },
  methods: {
    async fetchProfile() {
      this.loading = true;
      try {
        const { data } = await axios.get('/hr/self-service/profile');
        this.profile = data.data || null;
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Failed to load profile');
      } finally {
        this.loading = false;
      }
    },
    fmtDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
  },
};
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

.no-profile-box   { display:flex; flex-direction:column; align-items:center; padding:80px 20px; gap:16px; }
.no-profile-icon  { font-size:48px; color:#dde5f0; }
.no-profile-title { font-size:18px; font-weight:700; color:#1a2b45; margin:0; }
.no-profile-sub   { font-size:13px; color:#7a90b0; margin:0; text-align:center; max-width:360px; }

/* Skeleton */
.sk-profile { display:flex; gap:20px; align-items:center; background:#fff; border:1px solid #e4eaf2; border-radius:16px; padding:24px; margin-bottom:20px; }
.sk-avatar  { width:80px; height:80px; border-radius:50%; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; flex-shrink:0; }
.sk-lines   { flex:1; display:flex; flex-direction:column; gap:10px; }
.sk-line    { border-radius:4px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
.sk-line--lg { height:18px; width:60%; }
.sk-line--md { height:14px; width:40%; }
.sk-line--sm { height:12px; width:25%; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

/* Hero */
.profile-hero { display:flex; align-items:center; gap:20px; background:#fff; border:1px solid #e4eaf2; border-radius:16px; padding:24px; margin-bottom:20px; }
.avatar-wrap   { flex-shrink:0; }
.avatar-img    { width:80px; height:80px; border-radius:50%; object-fit:cover; border:3px solid #e0efff; }
.avatar-initials { width:80px; height:80px; border-radius:50%; background:#004AAD; color:#fff; font-size:28px; font-weight:700; display:flex; align-items:center; justify-content:center; letter-spacing:.04em; }
.profile-hero-info { flex:1; min-width:0; }
.profile-name  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 4px; }
.profile-role  { font-size:13px; color:#7a90b0; margin:0 0 10px; }
.profile-tags  { display:flex; gap:6px; flex-wrap:wrap; }
.tag           { display:inline-block; font-size:11px; font-weight:700; padding:3px 10px; border-radius:20px; text-transform:capitalize; }
.tag--dept     { background:#e0efff; color:#004AAD; }
.tag--grade    { background:#fef3c7; color:#b45309; }
.tag--green    { background:#dcfce7; color:#16a34a; }
.tag--gray     { background:#f1f5f9; color:#64748b; }

/* Sections */
.sections     { display:flex; flex-direction:column; gap:16px; }
.section-card { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:20px; }
.section-title { font-size:13px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#7a90b0; margin:0 0 16px; }

.details-grid  { display:grid; grid-template-columns:repeat(auto-fill,minmax(200px,1fr)); gap:16px; }
.detail-item   { display:flex; flex-direction:column; gap:4px; }
.detail-label  { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#aab8cc; }
.detail-val    { font-size:13px; font-weight:600; color:#1a2b45; }

/* Documents */
.docs-list { display:flex; flex-direction:column; gap:8px; }
.doc-item  { display:flex; align-items:center; gap:12px; padding:12px; border:1px solid #e4eaf2; border-radius:10px; text-decoration:none; transition:all .15s; color:inherit; }
.doc-item:hover { border-color:#004AAD; background:#f0f7ff; }
.doc-icon  { width:36px; height:36px; border-radius:8px; background:#e0efff; color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
.doc-info  { flex:1; min-width:0; }
.doc-name  { font-size:13px; font-weight:600; color:#1a2b45; margin:0 0 2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.doc-type  { font-size:11px; color:#7a90b0; margin:0; text-transform:uppercase; }
.doc-ext   { font-size:12px; color:#aab8cc; }
</style>
